import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, 
  Edit, 
  Check, 
  X, 
  Clock, 
  Calendar, 
  User, 
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Download,
} from 'lucide-react';
import { projectAPI } from '../services/api';
import { exportTimesheetPdf } from '../utils/timesheetPdf';
import toast from 'react-hot-toast';

const ManagerTimesheetDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  console.log('=== MANAGER TIMESHEET DETAILS COMPONENT LOADED ===');
  console.log('ManagerTimesheetDetails - ID from params:', id);
  console.log('ManagerTimesheetDetails - User role:', user?.role);
  console.log('ManagerTimesheetDetails - Current URL:', window.location.href);
  console.log('ManagerTimesheetDetails - Current pathname:', window.location.pathname);
  
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayDetails, setShowDayDetails] = useState(false);

  const { data: timesheet, isLoading, error } = useQuery(
    ['manager-timesheet', id],
    () => projectAPI.getManagerTimesheetById(id),
    {
      retry: false, // Don't retry on error (especially for 404s)
      onError: (error) => {
        const statusCode = error.response?.status;
        const isNotFound = statusCode === 404 || statusCode === 400;
        const isServerError = statusCode === 500 || statusCode >= 500;
        
        if (isNotFound) {
          toast.error('Timesheet not found. It may have been deleted.');
        } else if (isServerError) {
          toast.error('Server error occurred. Please try again later.');
        } else {
          toast.error('Failed to load timesheet details.');
        }
      },
      select: (response) => {
        console.log('=== MANAGER TIMESHEET DETAILS DEBUG ===');
        console.log('Full API response:', response);
        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);
        
        const data = response.data;
        console.log('Response data:', data);
        
        if (data && data.timesheet) {
          console.log('Timesheet data found:', data.timesheet);
          console.log('Timesheet ID:', data.timesheet.id);
          console.log('Timesheet status:', data.timesheet.status);
          console.log('Timesheet employee:', data.timesheet.employee);
          console.log('Timesheet entries:', data.timesheet.entries);

          const source = data.timesheet;
          const timesheetData = {
            id: source.id,
            employee: source.employee || null,
            startDate: source.startDate,
            endDate: source.endDate,
            totalHours: source.totalHours || 0,
            status: source.status,
            description: source.description || '',
            approvedBy: source.approvedBy || null,
            approvedAt: source.approvedAt || null,
            rejectionReason: source.rejectionReason || null,
            revocationReason: source.revocationReason || null,
            revokedBy: source.revokedBy || null,
            createdAt: source.createdAt || null,
            updatedAt: source.updatedAt || null,
            submittedAt: source.submittedAt || null,
            entries: [],
          };

          if (Array.isArray(source.entries)) {
            const mappedEntries = source.entries.map((entry) => {
              return {
                id: entry.id,
                date: entry.date ? new Date(entry.date).toISOString().split('T')[0] :
                      entry.entryDate ? new Date(entry.entryDate).toISOString().split('T')[0] : null,
                hours: entry.hours || entry.hoursWorked || 0,
                project: entry.project?.name || entry.projectName || entry.project || '',
                projectId: entry.project?.id || entry.projectId || null,
                description: entry.task || entry.taskDescription || entry.description || 'No description provided',
                taskName: entry.taskName || '',
                notes: entry.notes || '',
                startTime: entry.startTime || '',
                endTime: entry.endTime || '',
              };
            });

            timesheetData.entries = mappedEntries;
          }

          return timesheetData;
        } else {
          console.log('No timesheet data found in response');
          return null;
        }
      },
      enabled: !!id,
      retry: 1,
    }
  );

  const approveMutation = useMutation(
    async () => {
      console.log('=== APPROVE MUTATION CALLED ===');
      console.log('Calling projectAPI.approveManagerTimesheet with ID:', id);
      try {
        const result = await projectAPI.approveManagerTimesheet(id);
        console.log('=== API CALL SUCCESS ===');
        console.log('API Response:', result);
        return result;
      } catch (error) {
        console.error('=== API CALL FAILED ===');
        console.error('Network/API Error:', error);
        console.error('Error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        });
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        console.log('=== APPROVE SUCCESS ===');
        console.log('Success data:', data);
        queryClient.invalidateQueries(['manager-timesheet', id]);
        queryClient.invalidateQueries('manager-timesheets');
        toast.success('Timesheet approved successfully');
        setShowApprovalModal(false);
      },
      onError: (error) => {
        console.error('=== APPROVE ERROR ===');
        console.error('Approve error:', error);
        console.error('Error response:', error.response);
        console.error('Error message:', error.message);
        toast.error(error.response?.data?.message || 'Failed to approve timesheet');
      },
    }
  );

  const rejectMutation = useMutation(
    async () => {
      console.log('=== REJECT MUTATION CALLED ===');
      console.log('Calling projectAPI.rejectManagerTimesheet with ID:', id, 'Reason:', rejectionReason);
      try {
        const result = await projectAPI.rejectManagerTimesheet(id, rejectionReason);
        console.log('=== REJECT API CALL SUCCESS ===');
        console.log('API Response:', result);
        return result;
      } catch (error) {
        console.error('=== REJECT API CALL FAILED ===');
        console.error('Network/API Error:', error);
        console.error('Error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        });
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        console.log('=== REJECT SUCCESS ===');
        console.log('Success data:', data);
        queryClient.invalidateQueries(['manager-timesheet', id]);
        queryClient.invalidateQueries('manager-timesheets');
        toast.success('Timesheet rejected successfully');
        setShowApprovalModal(false);
        setRejectionReason('');
      },
      onError: (error) => {
        console.error('=== REJECT ERROR ===');
        console.error('Reject error:', error);
        console.error('Error response:', error.response);
        console.error('Error message:', error.message);
        toast.error(error.response?.data?.message || 'Failed to reject timesheet');
      },
    }
  );


  const handleApproval = (action) => {
    console.log('=== MANAGER APPROVAL DEBUG ===');
    console.log('Action:', action);
    console.log('Timesheet ID:', id);
    console.log('User role:', user?.role);
    setApprovalAction(action);
    setShowApprovalModal(true);
  };

  const handleConfirmApproval = () => {
    console.log('=== MANAGER CONFIRM APPROVAL DEBUG ===');
    console.log('Approval action:', approvalAction);
    console.log('Timesheet ID:', id);
    console.log('Rejection reason:', rejectionReason);
    
    if (approvalAction === 'approve') {
      // Close modal immediately for instant feel
      setShowApprovalModal(false);
      
      // Show instant success
      toast.success('Timesheet approved! Email notification sent.');
      
      // Navigate back immediately
      setTimeout(() => {
        navigate('/manager/timesheets');
      }, 500);
      
      // Call API in background
      console.log('Calling approveMutation.mutate()');
      approveMutation.mutate();
    } else if (approvalAction === 'reject') {
      if (!rejectionReason.trim()) {
        toast.error('Please provide a rejection reason');
        return;
      }
      
      // Close modal immediately
      setShowApprovalModal(false);
      
      // Show instant success
      toast.success('Timesheet rejected! Employee will be notified.');
      
      // Navigate back immediately
      setTimeout(() => {
        navigate('/manager/timesheets');
      }, 500);
      
      // Call API in background
      console.log('Calling rejectMutation.mutate()');
      rejectMutation.mutate();
    }
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setShowDayDetails(true);
  };

  const closeDayDetails = () => {
    setShowDayDetails(false);
    setSelectedDay(null);
  };

  const calculateTotalHours = (entries) => {
    return entries?.reduce((total, entry) => total + (entry.hours || 0), 0) || 0;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'PENDING':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    const statusCode = error.response?.status;
    const isNotFound = statusCode === 404 || statusCode === 400;
    const isServerError = statusCode === 500 || statusCode >= 500;
    const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to load timesheet details';
    const isDeleted = errorMessage.toLowerCase().includes('not found') || errorMessage.toLowerCase().includes('deleted');
    
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isNotFound || isDeleted ? 'Timesheet Not Found' : isServerError ? 'Server Error' : 'Error Loading Timesheet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {isDeleted || isNotFound 
              ? 'This timesheet may have been deleted or does not exist. Please navigate back to view other timesheets.'
              : isServerError
              ? 'An internal server error occurred while loading the timesheet. Please try again later or contact support if the problem persists.'
              : errorMessage}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Go Back
            </button>
            <button
              onClick={() => navigate('/manager/timesheets')}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              View All Timesheets
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!timesheet) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Timesheet Not Found</h3>
        <p className="text-gray-600 mb-4">The requested timesheet could not be found.</p>
        <button
          onClick={() => navigate('/manager/timesheets')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Back to Timesheets
        </button>
      </div>
    );
  }

  const totalHours = timesheet.entries?.reduce((total, entry) => total + (entry.hours || 0), 0) || 0;


  const handleExport = async () => {
    try {
      await exportTimesheetPdf(timesheet, { template: 'classic' });
    } catch (e) {
      console.error('Failed to export PDF', e);
      toast.error('Failed to export PDF');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-3">
              <FileText className="w-8 h-8 text-primary-600" />
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold text-gray-900">Manager Timesheet Details</h1>
                  <span className="px-3 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg border border-gray-300">
                    ID: {timesheet.id}
                  </span>
                </div>
                <p className="text-gray-600 mt-1">
                  {timesheet.employee?.firstName || 'Unknown'} {timesheet.employee?.lastName || 'Employee'} - Week of {timesheet.startDate ? new Date(timesheet.startDate).toLocaleDateString() : 'Unknown Date'}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExport}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export as PDF</span>
            </button>
            {(() => {
              console.log('=== BUTTON VISIBILITY DEBUG ===');
              console.log('Timesheet status:', timesheet.status);
              console.log('Should show buttons:', timesheet.status !== 'APPROVED' && timesheet.status !== 'REJECTED' && timesheet.status !== 'PENDING');
              return timesheet.status !== 'APPROVED' && timesheet.status !== 'REJECTED' && timesheet.status !== 'PENDING';
            })() && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleApproval('reject')}
                  disabled={rejectMutation.isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  <X className="w-4 h-4" />
                  <span>Reject</span>
                </button>
                <button
                  onClick={() => handleApproval('approve')}
                  disabled={approveMutation.isLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  <Check className="w-4 h-4" />
                  <span>Approve</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Timesheet Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Employee Information</h3>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Name:</span> {timesheet.employee?.firstName || 'Unknown'} {timesheet.employee?.lastName || 'Employee'}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Email:</span> {timesheet.employee?.email || 'Not available'}
              </p>
              {timesheet.assignedByManagerNames && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-primary-100 text-primary-800">
                    👤 Assigned by: {timesheet.assignedByManagerNames}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Timesheet Period</h3>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Start Date:</span> {timesheet.startDate ? new Date(timesheet.startDate).toLocaleDateString() : 'Invalid Date'}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">End Date:</span> {timesheet.endDate ? new Date(timesheet.endDate).toLocaleDateString() : 'Invalid Date'}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Total Hours:</span> {totalHours}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Status Information</h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                {getStatusIcon(timesheet.status)}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(timesheet.status)}`}>
                  {timesheet.status}
                </span>
              </div>
              {timesheet.submittedAt && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Submitted:</span> {new Date(timesheet.submittedAt).toLocaleDateString()} at {new Date(timesheet.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              <p className="text-sm text-gray-600">
                <span className="font-medium">Created:</span> {timesheet.createdAt ? new Date(timesheet.createdAt).toLocaleDateString() : 'Unknown'} {timesheet.createdAt && `at ${new Date(timesheet.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Updated:</span> {timesheet.updatedAt ? new Date(timesheet.updatedAt).toLocaleDateString() : 'Unknown'} {timesheet.updatedAt && `at ${new Date(timesheet.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
              </p>
              {timesheet.approvedAt && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Approved:</span> {new Date(timesheet.approvedAt).toLocaleDateString()} at {new Date(timesheet.approvedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              {timesheet.approvedBy && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Approved by:</span> {`${timesheet.approvedBy.firstName || ''} ${timesheet.approvedBy.lastName || ''}`.trim() || 'N/A'}
                  {timesheet.approvedBy.role && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 align-middle">
                      {typeof timesheet.approvedBy.role === 'object' ? (timesheet.approvedBy.role.name || 'MANAGER') : timesheet.approvedBy.role}
                    </span>
                  )}
                </p>
              )}
              {timesheet.rejectionReason && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-800">
                    <span className="font-medium">Rejection Reason:</span> {timesheet.rejectionReason}
                  </p>
                </div>
              )}
              {timesheet.revocationReason && (
                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-sm text-orange-800">
                    <span className="font-medium">Revocation Reason:</span> {timesheet.revocationReason}
                  </p>
                  {timesheet.revokedBy && (
                    <p className="text-xs text-orange-700 mt-1">
                      Revoked by: {timesheet.revokedBy?.firstName || ''} {timesheet.revokedBy?.lastName || ''}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Summary</h3>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Entries:</span> {timesheet.entries?.length || 0}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Projects:</span> {new Set(timesheet.entries?.map(e => e.project?.name || e.project).filter(Boolean)).size || 0}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Avg Hours/Day:</span> {(totalHours / 7).toFixed(1)}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Interactive Calendar View */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm border"
      >
        {/* Calendar Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Timesheet Calendar</h2>
              <p className="text-sm text-gray-600">Click on any day to view detailed entries</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Total Hours</div>
              <div className="text-xl font-bold text-blue-600">
                {calculateTotalHours(timesheet.entries).toFixed(1)}h
              </div>
            </div>
          </div>
        </div>
        
        {/* Calendar Grid */}
        <div className="p-6">
          {(() => {
            const startDate = new Date(timesheet.startDate);
            const endDate = new Date(timesheet.endDate);
            
            // Create calendar grid
            const calendarDays = [];
            const currentDate = new Date(startDate);
            
            // Add days before the start date to fill the week
            const startDayOfWeek = startDate.getDay();
            for (let i = 0; i < startDayOfWeek; i++) {
              const prevDate = new Date(startDate);
              prevDate.setDate(startDate.getDate() - (startDayOfWeek - i));
              calendarDays.push({ date: prevDate, isCurrentWeek: false });
            }
            
            // Add the actual week days
            while (currentDate <= endDate) {
              calendarDays.push({ date: new Date(currentDate), isCurrentWeek: true });
              currentDate.setDate(currentDate.getDate() + 1);
            }
            
            // Add remaining days to complete the week
            const endDayOfWeek = endDate.getDay();
            for (let i = endDayOfWeek + 1; i < 7; i++) {
              const nextDate = new Date(endDate);
              nextDate.setDate(endDate.getDate() + (i - endDayOfWeek));
              calendarDays.push({ date: nextDate, isCurrentWeek: false });
            }

            return (
              <div className="space-y-4">
                {/* Calendar Header */}
                <div className="grid grid-cols-7 gap-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="p-3 text-center text-sm font-medium text-gray-500 bg-gray-50 rounded-lg">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((dayData, index) => {
                    const day = dayData.date;
                    const dayEntries = timesheet.entries?.filter(entry => 
                      entry.date && new Date(entry.date).toDateString() === day.toDateString()
                    ) || [];
                    
                    const dayTotal = dayEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
                    const isToday = day.toDateString() === new Date().toDateString();
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    const hasEntries = dayEntries.length > 0;
                    
                    return (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => dayData.isCurrentWeek && handleDayClick(day)}
                        className={`min-h-[140px] p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                          dayData.isCurrentWeek 
                            ? hasEntries
                              ? 'bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300'
                              : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                            : 'bg-gray-50 border-gray-100 cursor-not-allowed opacity-50'
                        } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                      >
                        {/* Date Header */}
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-medium ${
                            dayData.isCurrentWeek 
                              ? isToday 
                                ? 'text-blue-600' 
                                : isWeekend 
                                  ? 'text-gray-500' 
                                  : 'text-gray-900'
                              : 'text-gray-400'
                          }`}>
                            {day.getDate()}
                          </span>
                          {dayTotal > 0 && (
                            <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                              {dayTotal.toFixed(1)}h
                            </span>
                          )}
                        </div>
                        
                        {/* Entries Preview */}
                        {dayData.isCurrentWeek && dayEntries.length > 0 && (
                          <div className="space-y-1">
                            {dayEntries.slice(0, 2).map((entry, entryIndex) => (
                              <div
                                key={entryIndex}
                                className="text-xs p-2 bg-white border border-blue-200 rounded truncate"
                                title={`${entry.project?.name || entry.project || 'No Project'}: ${entry.hours.toFixed(1)}h - ${entry.description || 'No description'}`}
                              >
                                <div className="font-medium text-blue-800 truncate">
                                  {entry.project?.name || entry.project || 'No Project'}
                                </div>
                                <div className="text-blue-600">
                                  {entry.hours.toFixed(1)}h
                                </div>
                              </div>
                            ))}
                            {dayEntries.length > 2 && (
                              <div className="text-xs text-gray-500 text-center bg-gray-100 rounded p-1">
                                +{dayEntries.length - 2} more
                              </div>
                            )}
                          </div>
                        )}
                        
                        {dayData.isCurrentWeek && dayEntries.length === 0 && (
                          <div className="text-xs text-gray-400 text-center mt-4">
                            {isWeekend ? 'Weekend' : 'No entries'}
                          </div>
                        )}
                        
                        {/* Click indicator */}
                        {dayData.isCurrentWeek && (
                          <div className="text-xs text-gray-400 text-center mt-2">
                            Click to view details
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Summary Stats */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {timesheet.entries?.length || 0}
              </div>
              <div className="text-sm text-gray-600">Total Entries</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {calculateTotalHours(timesheet.entries).toFixed(1)}h
              </div>
              <div className="text-sm text-gray-600">Total Hours</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {new Set(timesheet.entries?.map(e => e.project?.name || e.project).filter(Boolean)).size || 0}
              </div>
              <div className="text-sm text-gray-600">Projects</div>
            </div>
          </div>
        </div>

        {timesheet.entries?.length === 0 && (
          <div className="px-6 py-12 text-center">
            <Clock className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No time entries</h3>
            <p className="mt-1 text-sm text-gray-500">This timesheet has no time entries recorded.</p>
          </div>
        )}
      </motion.div>

      {/* Day Details Modal */}
      {showDayDetails && selectedDay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedDay.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {(() => {
                      const dayEntries = timesheet.entries?.filter(entry => 
                        entry.date && new Date(entry.date).toDateString() === selectedDay.toDateString()
                      ) || [];
                      const dayTotal = dayEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
                      return `${dayEntries.length} entries • ${dayTotal.toFixed(1)} hours`;
                    })()}
                  </p>
                </div>
                <button
                  onClick={closeDayDetails}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {(() => {
                const dayEntries = timesheet.entries?.filter(entry => 
                  entry.date && new Date(entry.date).toDateString() === selectedDay.toDateString()
                ) || [];
                
                if (dayEntries.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <Clock className="mx-auto h-12 w-12 text-gray-300" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No entries for this day</h3>
                      <p className="mt-1 text-sm text-gray-500">No time entries were recorded on this day.</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-4">
                    {dayEntries.map((entry, entryIndex) => (
                      <motion.div
                        key={entryIndex}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: entryIndex * 0.1 }}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-medium text-gray-900 text-lg break-words">
                            {entry.project?.name || entry.project || 'No Project'}
                          </h4>
                          <span className="text-lg font-bold text-green-600 flex-shrink-0 ml-2">
                            {entry.hours.toFixed(1)}h
                          </span>
                        </div>
                        
                        {entry.description && (
                          <div className="text-sm text-gray-700 mb-3">
                            {(() => {
                              const description = entry.description;
                              // Check if description contains bullet points (lines starting with • or -)
                              const lines = description.split('\n').filter(line => line.trim());
                              const hasBullets = lines.some(line => line.trim().match(/^[•\-\*]/));
                              
                              if (hasBullets) {
                                // Render as bullet list
                                return (
                                  <ul className="list-none space-y-1 ml-2">
                                    {lines.map((line, idx) => {
                                      const cleanLine = line.trim().replace(/^[•\-\*]\s*/, '');
                                      return cleanLine ? (
                                        <li key={idx} className="flex items-start">
                                          <span className="text-primary-600 mr-2 flex-shrink-0">•</span>
                                          <span className="break-words flex-1">{cleanLine}</span>
                                        </li>
                                      ) : null;
                                    })}
                                  </ul>
                                );
                              } else {
                                // Render as paragraph
                                return (
                                  <p className="break-words whitespace-pre-wrap overflow-wrap-anywhere">
                                    {description}
                                  </p>
                                );
                              }
                            })()}
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {entry.task && (
                            <div className="break-words">
                              <span className="font-medium text-gray-600">Task:</span>
                              <span className="ml-2 text-gray-900 break-words">{entry.task}</span>
                            </div>
                          )}
                          {(entry.startTime || entry.endTime) && (
                            <div>
                              <span className="font-medium text-gray-600">Time:</span>
                              <span className="ml-2 text-gray-900">
                                {entry.startTime && entry.endTime 
                                  ? `${entry.startTime} - ${entry.endTime}`
                                  : entry.startTime || entry.endTime
                                }
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {entry.notes && (
                          <div className="mt-3 p-3 bg-yellow-50 border-l-4 border-yellow-300 rounded">
                            <p className="text-sm text-yellow-800">
                              <strong>Note:</strong> {entry.notes}
                            </p>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                );
              })()}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={closeDayDetails}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}


      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {approvalAction === 'approve' ? 'Approve Timesheet' : 'Reject Timesheet'}
              </h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                {approvalAction === 'approve' 
                  ? 'Are you sure you want to approve this timesheet? This action cannot be undone.'
                  : 'Are you sure you want to reject this timesheet? The employee will be notified and can resubmit.'
                }
              </p>
              
              {approvalAction === 'reject' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Rejection *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a detailed reason for rejecting this timesheet..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    rows={3}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This reason will be visible to the employee when they view their timesheet.
                  </p>
                </div>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setRejectionReason('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmApproval}
                  disabled={approveMutation.isLoading || rejectMutation.isLoading || (approvalAction === 'reject' && !rejectionReason.trim())}
                  className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    approvalAction === 'approve' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {approveMutation.isLoading || rejectMutation.isLoading ? 'Processing...' : 
                   approvalAction === 'approve' ? 'Approve' : 'Reject'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ManagerTimesheetDetails;

